// apps/web/src/store/slices/authSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { AUTH_TOKEN } from "constants/AuthConstant";
import FirebaseService from "services/FirebaseService";
import AuthService from "services/AuthService";
import api from "services/api";

// ✅ onde vamos persistir o user (role/permissions + multi-empresa)
export const AUTH_USER = "AUTH_USER";
const ACTIVE_COMPANY_ID = "activeCompanyId";

function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Monta user completo para o front:
 * - garante companyId no localStorage (a partir do activeCompanyId)
 * - busca /companies/my para montar:
 *   - user.companies (lista de empresas)
 *   - user.companyAccess (role/perms da empresa ativa)
 */
async function buildUserContext(me) {
  const lsCompanyId = localStorage.getItem("companyId");
  const activeCompanyId = me?.activeCompanyId ? Number(me.activeCompanyId) : null;

  // se não tem companyId no localStorage mas o user tem activeCompanyId, salva
  if (!lsCompanyId && activeCompanyId) {
    localStorage.setItem("companyId", String(activeCompanyId));
  }

  // resolve companyId final
  const resolvedCompanyId =
    Number(localStorage.getItem("companyId")) || activeCompanyId || null;

  // busca empresas liberadas + role/perms
  let myCompanies = [];
  try {
    const res = await api.get(`/companies/my`);
    myCompanies = Array.isArray(res.data) ? res.data : [];
  } catch {
    myCompanies = [];
  }

  // myCompanies: [{ companyId, role, permissions, company }]
  const access = resolvedCompanyId
    ? myCompanies.find((x) => Number(x.companyId) === Number(resolvedCompanyId))
    : null;

  const companies = myCompanies.map((x) => x.company);

  // injeta no "me" para o front usar
  return {
    ...me,
    companies, // [{id,name,logoUrl}]
    companyAccess: access
      ? {
          companyId: Number(access.companyId),
          role: access.role,
          permissions: Array.isArray(access.permissions) ? access.permissions : [],
        }
      : null,
  };
}

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: localStorage.getItem(AUTH_TOKEN) || null,

  userLoading: false,
  user: safeJsonParse(localStorage.getItem(AUTH_USER)),
};

/**
 * ✅ Pega usuário logado + monta contexto multi-empresa
 */
export const fetchMe = createAsyncThunk(
  "auth/fetchMe",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState()?.auth?.token || localStorage.getItem(AUTH_TOKEN);
      if (!token) return null;

      const res = await api.get(`/auth/me`);
      const me = res.data;

      const user = await buildUserContext(me);

      localStorage.setItem(AUTH_USER, JSON.stringify(user));
      return user;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Não foi possível carregar usuário"
      );
    }
  }
);

/**
 * ✅ LOGIN:
 * - faz /auth/login
 * - salva token
 * - faz /auth/me
 * - monta contexto (companies + companyAccess)
 * - retorna { token, user }
 */
export const signIn = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;

    try {
      const response = await AuthService.login(email, password);

      const token = response?.access_token;
      if (!token) return rejectWithValue("Token não retornado no login");

      localStorage.setItem(AUTH_TOKEN, token);

      // ✅ importante: atualizar header do api imediatamente
      // (o interceptor já lê do localStorage, então só precisa salvar acima)

      const meRes = await api.get(`/auth/me`);
      const me = meRes.data;

      const user = await buildUserContext(me);

      // se não tem activeCompanyId, limpa companyId pra não quebrar guard
      if (!user?.activeCompanyId) {
        localStorage.removeItem("companyId");
      }

      localStorage.setItem(AUTH_USER, JSON.stringify(user));
      return { token, user };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Email ou senha inválidos"
      );
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;

    try {
      const response = await AuthService.register({ email, password });
      const token = response?.data?.token;

      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Error");
    }
  }
);

export const signOut = createAsyncThunk("auth/logout", async () => {
  const response = await FirebaseService.signOutRequest();

  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(AUTH_USER);
  localStorage.removeItem("companyId");
  localStorage.removeItem(ACTIVE_COMPANY_ID);

  return response.data;
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },

    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },

    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },

    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.user = null;

      localStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(AUTH_USER);
      localStorage.removeItem("companyId");
      localStorage.removeItem(ACTIVE_COMPANY_ID);

      state.redirect = "/auth/login";
    },

    showLoading: (state) => {
      state.loading = true;
    },

    setUser: (state, action) => {
      state.user = action.payload || null;

      if (state.user) {
        localStorage.setItem(AUTH_USER, JSON.stringify(state.user));
      } else {
        localStorage.removeItem(AUTH_USER);
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // ===== LOGIN =====
      .addCase(signIn.pending, (state) => {
        state.loading = true;
        state.showMessage = false;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";

        state.token = action.payload?.token || null;
        state.user = action.payload?.user || null;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })

      // ===== LOGOUT =====
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.redirect = "/auth/login";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.redirect = "/auth/login";
      })

      // ===== REGISTER =====
      .addCase(signUp.pending, (state) => {
        state.loading = true;
        state.showMessage = false;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })

      // ===== ME =====
      .addCase(fetchMe.pending, (state) => {
        state.userLoading = true;
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.userLoading = false;
        state.user = action.payload || null;
      })
      .addCase(fetchMe.rejected, (state) => {
        state.userLoading = false;
        state.token = null;
        state.user = null;
        state.redirect = "/auth/login";

        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(AUTH_USER);
        localStorage.removeItem("companyId");
        localStorage.removeItem(ACTIVE_COMPANY_ID);
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  setUser,
} = authSlice.actions;

export const selectAuthUser = (state) => state.auth.user;
export const selectAuthRole = (state) => state.auth.user?.role;

// ✅ agora permissões vêm da empresa ativa (companyAccess)
export const selectAuthPermissions = (state) =>
  state.auth.user?.companyAccess?.permissions || [];

export default authSlice.reducer;